type PopupDiv = HTMLDivElement & {
  showModal: () => void;
  close: () => void;
}

interface PopupManagerParams {
  div: HTMLDivElement;
  type: string;
  triggers?: string[];
  maxAge?: number;
}

export function managePopup({ div, type, triggers = ['exit-intent'], maxAge = 60 }: PopupManagerParams) {
  const popupShownCookie =
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`popup_${type}_shown`))
      ?.split('=')[1] || '';
  const popupShown = popupShownCookie === 'true';
  let popupShownThisPage = false;

  // If the div is not found or the popup has already been shown, return
  if (!div || (popupShown && !triggers.includes('dev'))) {
    return;
  }

  // Type the div as PopupDiv so it has additional methods
  const popupDiv = div as PopupDiv;

  popupDiv.showModal = () => {
    popupDiv.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  popupDiv.close = () => {
    popupDiv.classList.add('hidden');
    document.body.style.overflow = '';
  }

  popupDiv.addEventListener('click', event => {
    if (event.target === div) {
      popupDiv.close();
    }
  });

  popupDiv.querySelector('[data-close]')?.addEventListener('click', () => {
    popupDiv.close();
  });

  const showPopup = () => {
    if (popupDiv.classList.contains('hidden') && !popupShownThisPage) {
      popupDiv.showModal();
      popupShownThisPage = true;
      document.cookie = `popup_${type.replace(/-/g, '_')}_shown=true; max-age=${maxAge * 24 * 60 * 60}; path=/; secure; samesite=lax`;
      window.trackEventGA?.('[LivePlan] Exit Unit Engaged');
      window.trackEventAmplitude?.('[LivePlan] Exit Unit Engaged');
    }
  };

  let lastTouch = 0;
  let touchStart = 0;

  // Event listeners for different triggers
  // TODO - add scroll depth and time on page triggers
  triggers.forEach((trigger) => {
    // Exit intent
    if (trigger === 'exit-intent') {
      // Desktop support for when the mouse leaves the viewport
      document.addEventListener('mouseleave', (event) => {
        if (event.clientY <= 0) {
          showPopup();
        }
      });

      // Mobile support for when the user scrolls up quickly
      document.addEventListener('touchstart', (event) => {
        touchStart = event.touches[0].screenY;
      }, { passive: true });

      document.addEventListener('touchmove', (event) => {
        lastTouch = event.touches[0].screenY;
        const distance = lastTouch - touchStart;

        if (distance > 20 && touchStart - lastTouch < 0) {
          showPopup();
        }
      }, { passive: true });
    }

    // Dev trigger so popup shows on every page load
    if (trigger === 'dev') {
      showPopup();
    }

    // If showPopup=true is in the URL, show the popup
    if (trigger === 'show-popup') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('showPopup') === 'true') {
        showPopup();
      }
    }
  });
}
